import React, {useState} from "react"
import {useTranslation} from "react-i18next"
import {handleError, toastSuccess, toastWarning} from "helpers"
import {documentService, profileService} from "services"
import styles from "../../Documents.module.css"
import {UserDocument} from "types/user-documents"
import {BaseButton, BasePopup, SecondaryButton} from "components"
import {useMutation, useQuery} from "@tanstack/react-query"
import {Col, Divider, Row} from "antd"
import ReassignProfileDocumentsTable from "./ReassignProfileDocumentsTable"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {KlassDropdown} from "components/Select"

type PrintPreparePopupProps = {
    visible: boolean
    onClose: () => void
    documents: UserDocument[]
}

const ReassignProfilePopup = ({visible, onClose, documents}: PrintPreparePopupProps) => {
    const {t} = useTranslation(["common"])
    const [selectedProfile, setSelectedProfile] = useState(null)
    const ownerUserId = documents?.[0]?.ownerUserId
    const {data: profilesData} = useQuery(
        [`reassign-profile-user-documents`, ownerUserId],
        async () =>
            profileService.getAll({
                range: {limit: UNLIMITED_PAGE_SIZE, offset: 0},
                filter: {userId: [ownerUserId]}
            }),
        {
            enabled: Boolean(ownerUserId)
        }
    )

    const reassignProfileMutation = useMutation(
        async () => {
            const userDocumentIds = documents.map((item) => item.userDocumentId)
            if (!userDocumentIds.length) {
                toastWarning("Documents not found")
                return
            }
            if (!selectedProfile) {
                toastWarning("Select a profile to reassign")
                return
            }
            return await documentService.reassignProfile({
                userDocumentIds,
                toOwnerProfileId: selectedProfile
            })
        },
        {
            onSuccess: (res) => {
                if (res) {
                    toastSuccess("Successfully reassigned profile in the documents")
                    onClose()
                }
            },
            onError: (error) => handleError(error)
        }
    )

    return (
        <BasePopup isShow={visible} onClose={onClose} width="90vw" leftIcon="FILE_ADD" leftIconColor="#fff">
            <div className={styles.wrap}>
                <div className={styles.popupTitle}>Confirm reassign profile</div>
                <Divider />
                <div className={styles.popupHeading}>
                    Are you sure you want to reassign profile for the following documents?
                </div>
                <div className={styles.popupBody}>
                    <div className="mt-24">Select the new profile to reassign</div>
                    <Row>
                        <Col span={6}>
                            <KlassDropdown
                                key={ownerUserId}
                                placeholder={"Select profile"}
                                options={profilesData?.data || []}
                                valueKey="id"
                                labelKey="customProfileId"
                                getOptionLabel={(option) => `${option.customProfileId} - [${option.type}]`}
                                onChange={(val) => {
                                    setSelectedProfile(val?.id)
                                }}
                            />
                        </Col>
                    </Row>
                </div>
                <div className="mt-24">
                    <div className={styles.popupHeading}>Documents to Reassign</div>
                    <br />
                    <ReassignProfileDocumentsTable documents={documents} moveItem={() => {}} isDraggable={false} />
                </div>
                <Divider />
                <Row gutter={12} justify="end">
                    <Col>
                        <SecondaryButton
                            onClick={onClose}
                            title={t("common:action.cancel")}
                            loading={reassignProfileMutation.isLoading}
                        />
                    </Col>
                    <Col>
                        <BaseButton
                            onClick={reassignProfileMutation.mutate}
                            title={t("common:action.confirm")}
                            loading={reassignProfileMutation.isLoading}
                        />
                    </Col>
                </Row>
            </div>
        </BasePopup>
    )
}

export default ReassignProfilePopup
